import React from "react";

import ReconFixSteps from "../components/ReconFixSteps";
import ReconTxnInvestigate from "../components/ReconTxnInvestigate";

export const STATUS = {
  MATCHED: "MATCHED",
  MISMATCHED: "MISMATCHED",
  PENDING_REVIEW: "PENDING_REVIEW",
};

export function StatusBadge({ status, small }) {
  const s = (status || "").toUpperCase();
  let cls = "recon-badge--neutral";
  let label = s || "—";
  if (s === STATUS.MATCHED) {
    cls = "recon-badge--matched";
    label = "Matched";
  } else if (s === STATUS.MISMATCHED) {
    cls = "recon-badge--mismatched";
    label = "Mismatched";
  } else if (s === STATUS.PENDING_REVIEW) {
    cls = "recon-badge--pending";
    label = "Pending review";
  }
  return <span className={`recon-badge ${cls} ${small ? "recon-badge--sm" : ""}`}>{label}</span>;
}

export function KpiCard({ label, value, hint, variant = "neutral" }) {
  return (
    <div className={`recon-kpi recon-kpi--${variant}`}>
      <div className="recon-kpi__label">{label}</div>
      <div className="recon-kpi__value">{value}</div>
      {hint ? <div className="recon-kpi__hint">{hint}</div> : null}
    </div>
  );
}

export function formatBdt(val, unit) {
  if (val === "—" || val == null || val === "") return "—";
  if (unit === "BDT") return `${val} BDT`;
  if (unit === "shares") return `${val} sh`;
  if (unit === "rows") return `${val} row(s)`;
  return String(val);
}

export function deltaClass(delta, unit) {
  if (!delta || delta === "0" || delta === "0.00") return "recon-compare-table__delta--ok";
  const n = parseFloat(delta);
  if (Number.isNaN(n) || n === 0) return "recon-compare-table__delta--ok";
  return "recon-compare-table__delta--bad";
}

export function ReconciliationProjectDetail({
  detail,
  loading,
  backfillBusy,
  backfillMsg,
  capRepairBusy,
  capRepairMsg,
  onBackfill,
  onCapRepair,
  onExportMissing,
  onRemediationAction,
  fullPage = false,
}) {
  const shellClass = fullPage ? "recon-detail recon-detail--page" : "recon-detail";

  if (loading) {
    return (
      <div className={shellClass}>
        <div className="recon-loading">Loading project reconciliation…</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className={shellClass}>
        <div className="recon-detail__empty">
          <p>Could not load reconciliation data for this project.</p>
        </div>
      </div>
    );
  }

  const overall = (detail.overall_status || "").toUpperCase();
  const bannerClass =
    overall === STATUS.MATCHED
      ? "recon-detail__banner--matched"
      : overall === STATUS.PENDING_REVIEW
        ? "recon-detail__banner--pending"
        : "recon-detail__banner--mismatched";

  const checks = detail.checks || [];

  return (
    <div className={shellClass}>
      <div className={`recon-detail__banner ${bannerClass}`}>
        <h1 className="recon-detail__title">
          {detail.project_code || detail.project_id}
          <span className="recon-detail__subtitle">· {detail.project_name}</span>
        </h1>
        <div className="recon-detail__meta">
          <StatusBadge status={overall} />
          <span className="muted small">Lifecycle: {detail.status}</span>
        </div>
        {detail.issue_labels?.length ? (
          <div className="recon-issue-chips">
            {detail.issue_labels.map((lbl) => (
              <span key={lbl} className="recon-issue-chip">
                {lbl}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="recon-detail__body">
        {detail.remediation_plans?.length ? (
          <ReconFixSteps plans={detail.remediation_plans} onUiAction={onRemediationAction} />
        ) : null}

        <div className="recon-mini-metrics">
          <div className="recon-mini-metric">
            <div className="recon-mini-metric__label">Fund ledger (system)</div>
            <div className="recon-mini-metric__val">{detail.fund_ledger_balance_bdt} BDT</div>
          </div>
          <div className="recon-mini-metric">
            <div className="recon-mini-metric__label">Expected (transactions)</div>
            <div className="recon-mini-metric__val">{detail.expected_balance_including_adjustments_bdt} BDT</div>
          </div>
          <div className="recon-mini-metric">
            <div className="recon-mini-metric__label">Cash delta</div>
            <div className={`recon-mini-metric__val ${deltaClass(detail.fund_ledger_delta_bdt, "BDT")}`}>
              {detail.fund_ledger_delta_bdt} BDT
            </div>
          </div>
          <div className="recon-mini-metric">
            <div className="recon-mini-metric__label">NAV fund value</div>
            <div className="recon-mini-metric__val">{detail.total_fund_value_nav_bdt} BDT</div>
          </div>
        </div>

        <h2 className="recon-section-title">Verification checks — system vs expected</h2>
        <div className="recon-table-scroll">
          <table className="recon-compare-table">
            <thead>
              <tr>
                <th>Check</th>
                <th>System record</th>
                <th>Expected / ledger</th>
                <th>Δ</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {checks.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="recon-compare-table__check-title">{c.title}</div>
                    <div className="recon-compare-table__check-desc">{c.description}</div>
                    {c.detail ? <div className="recon-compare-table__check-desc">{c.detail}</div> : null}
                    {c.remediation?.likely_cause_label ? (
                      <div className="recon-compare-table__fix-hint">
                        Fix: {c.remediation.likely_cause_label} — see steps above
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="muted small">{c.system_label}</div>
                    <div className="recon-compare-table__value">{formatBdt(c.system_value, c.unit)}</div>
                  </td>
                  <td>
                    <div className="muted small">{c.expected_label}</div>
                    <div className="recon-compare-table__value">{formatBdt(c.expected_value, c.unit)}</div>
                  </td>
                  <td className={deltaClass(c.delta, c.unit)}>{formatBdt(c.delta, c.unit)}</td>
                  <td>
                    <StatusBadge status={c.status} small />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {detail.missing_fund_ledger_entries?.length > 0 ? (
          <>
            <h2 className="recon-section-title">Unmatched transactions — pending ledger rows</h2>
            <p className="muted small" style={{ margin: "0 0 8px" }}>
              These completed share transactions do not yet have a fund ledger entry. Status:{" "}
              <StatusBadge status={STATUS.PENDING_REVIEW} small />
            </p>
            <div className="recon-table-scroll">
              <table className="recon-txn-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Direction</th>
                    <th>Amount</th>
                    <th>When</th>
                    <th>Investigate</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.missing_fund_ledger_entries.map((m) => (
                    <tr key={m.reference_id}>
                      <td>{m.type}</td>
                      <td>{m.direction}</td>
                      <td>{m.amount_bdt} BDT</td>
                      <td>{m.at ? new Date(m.at).toLocaleString() : "—"}</td>
                      <td>
                        <ReconTxnInvestigate txn={m} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="history-btn history-btn--secondary"
              style={{ marginTop: 10 }}
              onClick={onExportMissing}
            >
              Export missing rows (CSV)
            </button>
          </>
        ) : null}

        <h2 className="recon-section-title">Share & redemption context</h2>
        <div className="recon-table-scroll">
          <table className="recon-compare-table">
            <tbody>
              <tr>
                <td className="recon-compare-table__check-title">Shares sold (system)</td>
                <td className="recon-compare-table__value">{detail.shares_sold}</td>
                <td className="recon-compare-table__value">Purchase ledger: {detail.primary_purchase_shares_ledger}</td>
                <td
                  className={
                    detail.shares_sold_delta ? "recon-compare-table__delta--bad" : "recon-compare-table__delta--ok"
                  }
                >
                  {detail.shares_sold_delta || "0.00"}
                </td>
                <td>
                  <StatusBadge status={detail.shares_sold_delta ? STATUS.MISMATCHED : STATUS.MATCHED} small />
                </td>
              </tr>
              <tr>
                <td className="recon-compare-table__check-title">Holdings vs issued</td>
                <td colSpan={2}>
                  Held {detail.cap_table?.shares_held_all_holders} sh (investors{" "}
                  {detail.cap_table?.shares_held_investors}, treasury {detail.cap_table?.shares_held_treasury})
                  {detail.cap_table?.shares_pending_redemption !== "0.00" ? (
                    <> · Pending redemption {detail.cap_table?.shares_pending_redemption} sh</>
                  ) : null}
                </td>
                <td>Expected {detail.cap_table?.expected_issued_shares} sh</td>
                <td>
                  <StatusBadge
                    status={detail.cap_table?.cap_table_matches_cap ? STATUS.MATCHED : STATUS.MISMATCHED}
                    small
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {!detail.cap_table?.cap_table_matches_cap &&
        detail.remediation_plans?.find((p) => p.issue_code === "cap_table_mismatch") ? (
          <p className="recon-compare-table__fix-hint" style={{ marginTop: 8 }}>
            {detail.remediation_plans.find((p) => p.issue_code === "cap_table_mismatch").likely_cause_label}
            {" — "}
            {detail.remediation_plans.find((p) => p.issue_code === "cap_table_mismatch").summary}
          </p>
        ) : null}

        <div className="recon-actions recon-actions--page">
          <button
            type="button"
            className="history-btn history-btn--secondary"
            disabled={backfillBusy || !detail.missing_fund_ledger_entry_count}
            onClick={() => onBackfill(true)}
          >
            Preview backfill
          </button>
          <button
            type="button"
            className="history-btn history-btn--primary"
            disabled={backfillBusy || !detail.missing_fund_ledger_entry_count}
            onClick={() => onBackfill(false)}
          >
            {backfillBusy ? "Working…" : "Fix missing ledger rows"}
          </button>
          {detail.cap_table_auto_fix?.eligible ? (
            <>
              <button
                type="button"
                className="history-btn history-btn--secondary"
                disabled={capRepairBusy}
                onClick={() => onCapRepair(true)}
              >
                Preview cap-table fix
              </button>
              <button
                type="button"
                className="history-btn history-btn--primary"
                disabled={capRepairBusy}
                onClick={() => onCapRepair(false)}
              >
                {capRepairBusy ? "Working…" : "Auto-fix cap table"}
              </button>
            </>
          ) : detail.issue_codes?.includes("cap_table_mismatch") ? (
            <span className="muted small recon-actions__hint">
              Cap-table auto-fix not available
              {detail.cap_table_auto_fix?.reason
                ? ` (${String(detail.cap_table_auto_fix.reason).replace(/_/g, " ")})`
                : ""}
              — use manual steps above.
            </span>
          ) : null}
        </div>
        {backfillMsg ? <p className="muted small recon-feedback">{backfillMsg}</p> : null}
        {capRepairMsg ? <p className="muted small recon-feedback">{capRepairMsg}</p> : null}
      </div>
    </div>
  );
}
